import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import useTitanApp from "../../../hooks/useTitanApp";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import {
  addPatientConsultationNotes,
  setIsFinalSummary,
  setPatientNotes,
  setIsNotesExpanded,
} from "../../../dispatchers/patientDispatchers";

export default function NotesPanel({ onHeightChange }) {
  const { appState, patientState } = useTitanApp();
  const { consultationId, isFinalSummary, PatientNotes, isNotesExpanded } =
    patientState || {};
  const { darkMode } = appState;
  const [height, setHeight] = useState(150);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(0);
  const [notes, setNotes] = useState(PatientNotes);
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);

  const panelRef = useRef(null);

  const minHeight = 150;
  const maxHeight = 400;

  useLayoutEffect(() => {
    if (!panelRef.current) return;

    const updateHeight = () => {
      const currentHeight = panelRef.current?.offsetHeight || 0;
      setHeight(currentHeight);
      if (typeof onHeightChange === "function") {
        onHeightChange(currentHeight);
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(panelRef.current);

    return () => observer.disconnect();
  }, [onHeightChange]);

  useEffect(() => {
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartY(e.clientY);
    setStartHeight(height);

    const handleMouseMove = (e) => {
      const deltaY = startY - e.clientY;
      const newHeight = Math.min(
        maxHeight,
        Math.max(minHeight, startHeight + deltaY)
      );
      setHeight(newHeight);

      if (newHeight > minHeight && !isNotesExpanded) {
        setIsNotesExpanded(true);
      } else if (newHeight <= minHeight && isNotesExpanded) {
        setIsNotesExpanded(false);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
  };

  const toggleExpanded = () => {
    if (isNotesExpanded) {
      setIsNotesExpanded(false);
      setHeight(minHeight);
    } else {
      setIsNotesExpanded(true);
      setHeight(250);
    }
  };

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor:
            theme.palette.mode === "dark" ? "#525252" : "#363636",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(["width"], {
        duration: 200,
      }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,.35)"
          : "rgba(0,0,0,.25)",
      boxSizing: "border-box",
    },
  }));

  const saveDebounceRef = useRef(null);

  const autoSaveNotes = (value) => {
    if (saveDebounceRef.current) {
      clearTimeout(saveDebounceRef.current);
    }

    // Save after 1 second of inactivity
    saveDebounceRef.current = setTimeout(() => {
      if (consultationId && value.trim()) {
        addPatientConsultationNotes({
          consultationId: consultationId,
          notes: value,
        });
        setPatientNotes(value);
      }
    }, 1000);
  };

  const handleNotesBlur = () => {
    if (!consultationId || !notes.trim()) return;

    addPatientConsultationNotes({
      consultationId,
      notes,
    });

    setPatientNotes(notes);
  };


  return (
    <div
      ref={panelRef}
      className={`relative w-full border-t transition-colors duration-300 overflow-hidden ${
        darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
      style={{
        height: `${height}px`,
        transition: isDragging ? "none" : "height 0.2s ease",
      }}
    >
      <div
        className={`relative h-8 cursor-ns-resize flex items-center justify-center border-b transition-all duration-200 select-none user-select-none ${
          isDragging
            ? darkMode
              ? "bg-gray-600 border-gray-500"
              : "bg-gray-200 border-gray-300"
            : darkMode
            ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
            : "bg-gray-100 border-gray-200 hover:bg-gray-200"
        }`}
        onMouseDown={handleMouseDown}
      >
        <div className="drag-indicator">
          <GripVertical
            className={`w-5 h-5 transition-colors duration-200 ${
              isDragging
                ? darkMode
                  ? "fill-blue-300"
                  : "fill-blue-600"
                : darkMode
                ? "fill-gray-400"
                : "fill-gray-500"
            }`}
            color={darkMode ? "#fff" : "#000"}
          />
          <span
            className={`text-sm font-medium transition-colors duration-200 ${
              isDragging
                ? darkMode
                  ? "text-blue-200"
                  : "text-blue-800"
                : darkMode
                ? "text-gray-300"
                : "text-gray-700"
            }`}
          >
            Extra notes
          </span>
          <GripVertical
            className={`w-5 h-5 transition-colors duration-200 ${
              isDragging
                ? darkMode
                  ? "text-blue-300"
                  : "text-blue-600"
                : darkMode
                ? "text-gray-400"
                : "text-gray-500"
            }`}
            color={darkMode ? "#fff" : "#000"}
          />
        </div>

        <button className="toggle-btn" onClick={toggleExpanded}>
          {isNotesExpanded ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronUp size={16} />
          )}
        </button>
      </div>

      <div className="panel-content">
        <div className="content-header">
          <h3>Notes</h3>
          <div className="summary-toggle">
            {isLimitExceeded && (
              <span style={{ 
                color: '#ef4444', 
                fontSize: '14px', 
                fontWeight: 'bold',
                fontFamily: '"SFPro-Rounded", sans-serif',
                marginRight: '12px'
              }}>
                Note limit reached (50,000 max)
              </span>
            )}
            <p>Add to final summary</p>
            <div className="switch-placeholder">
              <AntSwitch
                inputProps={{ "aria-label": "ant design" }}
                onChange={(event) => {
                  setIsFinalSummary(event.target.checked);
                }}
                checked={isFinalSummary}
              />
            </div>
          </div>
        </div>

        <div className="textarea-wrapper">
          <div className="form-group notes-form-group">
            <textarea
              className="template_input text_input_field"
              type="text"
              autoComplete="off"
              value={notes}
              onChange={(e) => {
                let value = e.target.value;
                value = value.replace(/`/g, '');
                
                if (value.length > 50000) {
                  // Only keep first 50,000 characters
                  setNotes(value.substring(0, 50000));
                  setIsLimitExceeded(true);
                } else {
                  setNotes(value);
                  setIsLimitExceeded(false);
                }
              }}
               onBlur={handleNotesBlur}
              placeholder="Type your consultation notes here..."
              style={{ minHeight: `${height - 80}px` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
